import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, errMsg, newIdemKey, type TradingNote, type TradingSummary } from "../api";
import { CheckCircleIcon } from "../components/Icons";
import { HistoryModal } from "../components/HistoryModal";
import { ImportNoteDialog } from "../components/ImportNoteDialog";
import { Modal } from "../components/Modal";
import { RowMenu } from "../components/RowMenu";
import { Btn, Field, Input, Notice, PageHead } from "../components/ui";
import { useEntities } from "../entity";
import { fmtDay, formatMoney } from "../lib/money";
import { usePeriod } from "../period";
import { useMe } from "../session";

const brl = (m: { decimal: string }) => formatMoney(m.decimal, "BRL");
const sign = (m: { decimal: string }) => (m.decimal.startsWith("-") ? "neg" : BigInt(m.decimal.replace(".", "")) === 0n ? "" : "pos");

const sumDec = (a: number, b: number) => { const t = BigInt(a) + BigInt(b); const s = t.toString().padStart(3, "0"); return `${s.slice(0, -2)}.${s.slice(-2)}`; };

/** Investimentos — Futuros: resultado de trading a partir das notas de corretagem (BM&F). */
export function Investments() {
  const { me } = useMe();
  const realm = me.user.realm;
  const { current } = useEntities();
  const per = usePeriod();
  const [sp, setSp] = useSearchParams();
  const [sum, setSum] = useState<TradingSummary | null>(null);
  const [notes, setNotes] = useState<TradingNote[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [ok, setOk] = useState<string | null>(null);
  const [importing, setImporting] = useState(false);
  const [detail, setDetail] = useState<TradingNote | null>(null);
  const [del, setDel] = useState<TradingNote | null>(null);
  const [hist, setHist] = useState<TradingNote | null>(null);
  const [showCancelled, setShowCancelled] = useState(false);

  const load = useCallback(async () => {
    if (!current) return;
    try {
      const [s, n] = await Promise.all([
        api<TradingSummary>("GET", `/api/${realm}/trading/summary?entityId=${current.id}&from=${per.from}&to=${per.to}`),
        api<TradingNote[]>("GET", `/api/${realm}/trading-notes?entityId=${current.id}&from=${per.from}&to=${per.to}${showCancelled ? "" : "&status=ativa"}`),
      ]);
      setSum(s); setNotes(n); setErr(null);
    } catch (e) { setErr(errMsg(e)); }
  }, [realm, current, per.from, per.to, showCancelled]);
  useEffect(() => { void load(); }, [load]);

  const reconcile = async (n: TradingNote) => {
    try { await api("POST", `/api/${realm}/trading-notes/${n.id}/conciliar`, {}, newIdemKey()); setOk(`Nota ${n.noteNumber} conciliada.`); await load(); } catch (e) { setErr(errMsg(e)); }
  };
  const openDetail = useCallback(async (id: string) => {
    try { setDetail(await api<TradingNote>("GET", `/api/${realm}/trading-notes/${id}`)); } catch (e) { setErr(errMsg(e)); }
  }, [realm]);
  // "Abrir nota" a partir de um lançamento
  const noteParam = sp.get("note");
  useEffect(() => {
    if (!noteParam) return;
    void api<TradingNote>("GET", `/api/${realm}/trading-notes/${noteParam}`).then((n) => { per.goTo(n.tradeDate); setDetail(n); }).catch((e) => setErr(errMsg(e)));
    const n = new URLSearchParams(sp); n.delete("note"); setSp(n, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [noteParam]);

  if (!current) return <div className="page"><PageHead title="Investimentos" />{err ? <Notice kind="error">{err}</Notice> : <p className="muted">Carregando…</p>}</div>;
  const month = new Intl.DateTimeFormat("pt-BR", { month: "long", year: "numeric" }).format(per.period);

  return (
    <div className="page investments-page">
      <PageHead title="Investimentos — Notas" sub="Importação e extrato das notas de corretagem. Resultado de trading a partir das notas de corretagem (mini índice, mini dólar e outros contratos da B3)." />
      {ok && <Notice kind="ok">{ok}</Notice>}
      {err && <Notice kind="error">{err}</Notice>}
      <div className="inv-actions">
        <Btn onClick={() => setImporting(true)} data-import-note-open>Importar nota de corretagem</Btn>
        <span className="muted small" style={{ marginLeft: 12 }}>Período: {month} (use o seletor de mês do cabeçalho)</span>
      </div>

      {sum && (
        <div className="inv-kpis" data-trading-kpis>
          <div className="inv-box"><div className="small muted">Resultado líquido</div><div className={`kpi-v ${sign(sum.net)}`} data-kpi="net">{brl(sum.net)}</div></div>
          <div className="inv-box"><div className="small muted">Resultado bruto</div><div className={`kpi-v ${sign(sum.gross)}`} data-kpi="gross">{brl(sum.gross)}</div></div>
          <div className="inv-box"><div className="small muted">Corretagem e taxas</div><div className="kpi-v" data-kpi="costs">{brl(sum.costs)}</div></div>
          <div className="inv-box"><div className="small muted">IRRF retido</div><div className="kpi-v" data-kpi="irrf">{brl(sum.irrf)}</div></div>
          <div className="inv-box"><div className="small muted">Dias operados</div><div className="kpi-v" data-kpi="days">{sum.days}</div><div className="small muted">{sum.days ? `${sum.winDays} positivos (${Math.round((sum.winDays / sum.days) * 100)}%)` : "—"}</div></div>
          <div className="inv-box"><div className="small muted">Notas / contratos</div><div className="kpi-v" data-kpi="notes">{sum.notes} / {sum.contracts}</div></div>
        </div>
      )}

      <div className="note-sections">
        {sum && sum.byAsset.length > 0 && (
          <section className="card table-card">
            <table aria-label="Resultado por ativo" data-by-asset>
              <thead><tr><th>Ativo</th><th className="num">Operações</th><th className="num">Contratos</th><th className="num">Resultado</th><th className="num">Custos</th><th className="num">Líquido</th></tr></thead>
              <tbody>{sum.byAsset.map((a) => (
                <tr key={a.assetCode} data-asset={a.assetCode}><td><strong>{a.name}</strong></td><td className="num">{a.trades}</td><td className="num">{a.contracts}</td>
                  <td className={`num tabular ${sign(a.result)}`}>{brl(a.result)}</td><td className="num tabular">− {brl(a.costs)}</td><td className={`num tabular ${sign(a.net)}`}><b>{brl(a.net)}</b></td></tr>))}</tbody>
            </table>
          </section>
        )}

        <section className="card table-card">
          {notes && notes.length === 0 && <div className="empty-box"><p>Nenhuma nota de corretagem neste mês.</p></div>}
          {notes && notes.length > 0 && (
            <table aria-label="Notas de corretagem" data-notes-table>
              <thead><tr><th>Pregão</th><th>Nota</th><th>Corretora</th><th>Ativos</th><th className="num">Resultado</th><th className="num">Custos + IRRF</th><th className="num">Líquido</th><th>Situação</th><th aria-label="Ações" /></tr></thead>
              <tbody>{notes.map((n) => (
                <tr key={n.id} data-note-row={n.noteNumber} style={n.status === "cancelada" ? { opacity: 0.55 } : undefined}>
                  <td className="tabular">{fmtDay(n.tradeDate)}</td>
                  <td>{n.noteNumber}{n.status === "cancelada" && <span className="pill" style={{ marginLeft: 6 }}>Excluída</span>}</td>
                  <td className="small">{n.brokerName.split(/\s+/).slice(0, 2).join(" ")}<div className="muted">{n.accountName}</div></td>
                  <td className="small">{n.assets.join(", ")}</td>
                  <td className={`num tabular ${sign(n.gross)}`}>{brl(n.gross)}</td>
                  <td className="num tabular">− {brl({ decimal: sumDec(n.costs.cents, n.irrf.cents) })}</td>
                  <td className={`num tabular ${sign(n.net)}`}><b>{brl(n.net)}</b></td>
                  <td data-note-status>{n.status === "cancelada" ? <span className="pill">Excluída</span> : (<>
                    <span className="sit realizado" title="Confirmado: lançamentos efetivados na conta" data-note-confirmed><CheckCircleIcon size={18} /></span>{" "}
                    {n.reconState === "conciliada" ? <span className="pill recon" data-recon-state="conciliado">Conciliada</span>
                      : n.reconState === "parcial" ? <span className="pill recon partial" data-recon-state="parcial">Parcial</span> : <span className="muted small" data-recon-state="pendente">—</span>}</>)}</td>
                  <td>
                    <RowMenu label={`Nota ${n.noteNumber}`}>
                      <button className="link" onClick={() => void openDetail(n.id)} data-act="details">Detalhes</button>
                      <a className="link" href={`/api/${realm}/trading-notes/${n.id}/pdf`} target="_blank" rel="noreferrer" data-act="pdf">Ver PDF</a>
                      {n.status === "ativa" && n.reconState !== "conciliada" && <button className="link" onClick={() => void reconcile(n)} data-act="reconcile">Marcar como conciliada</button>}
                      {n.status === "ativa" && <button className="link danger" onClick={() => setDel(n)} data-act="delete">Excluir</button>}
                      <button className="link" onClick={() => setHist(n)} data-act="history">Histórico</button>
                    </RowMenu>
                  </td>
                </tr>))}</tbody>
            </table>
          )}
          <label className="small" style={{ display: "block", padding: "8px 12px" }}><input type="checkbox" checked={showCancelled} onChange={(e) => setShowCancelled(e.target.checked)} /> Mostrar notas excluídas</label>
        </section>
      </div>

      {importing && <ImportNoteDialog entityId={current.id} onClose={() => setImporting(false)} onDone={(n) => { setImporting(false); setOk(`Nota ${n.noteNumber} importada: líquido de ${brl(n.net)} lançado na conta ${n.accountName}.`); per.goTo(n.tradeDate); void load(); }} />}
      {detail && <NoteDetail note={detail} realm={realm} onClose={() => setDetail(null)} />}
      {hist && <HistoryModal title={`Histórico — nota ${hist.noteNumber}`} path={`/api/${realm}/trading-notes/${hist.id}/history`} onClose={() => setHist(null)} />}
      {del && <DeleteNote note={del} onClose={() => setDel(null)} onConfirm={async (reason) => {
        try { await api("POST", `/api/${realm}/trading-notes/${del.id}/cancel`, { version: del.version, reason }, newIdemKey()); setOk(`Nota ${del.noteNumber} excluída; os lançamentos foram cancelados.`); setDel(null); await load(); }
        catch (e) { setErr(errMsg(e)); setDel(null); }
      }} />}
    </div>
  );
}

function NoteDetail({ note, realm, onClose }: { note: TradingNote; realm: string; onClose: () => void }) {
  return (
    <Modal title={`Nota ${note.noteNumber} — ${fmtDay(note.tradeDate)}`} onClose={onClose} wide>
      <div data-note-detail>
        <div className="inv-box" style={{ marginTop: 0 }}>
          <div className="inv-row"><span>Corretora</span><b>{note.brokerName}</b></div>
          <div className="inv-row"><span>Conta</span><span>{note.accountName}</span></div>
          <div className="inv-row"><span>Resultado do dia</span><b className={sign(note.gross)}>{brl(note.gross)}</b></div>
          <div className="inv-row"><span>Corretagem e taxas</span><span>− {brl(note.costs)}</span></div>
          <div className="inv-row"><span>IRRF retido</span><span>− {brl(note.irrf)}</span></div>
          <div className="inv-row big"><span>Líquido da nota</span><b className={sign(note.net)}>{brl(note.net)}</b></div>
          {note.status === "cancelada" && <p className="small muted">Nota excluída: {note.cancelReason}</p>}
        </div>
        <div className="card table-card"><table className="note-trades" aria-label="Operações">
          <thead><tr><th>C/V</th><th>Contrato</th><th>Venc.</th><th className="num">Qtde</th><th className="num">Preço</th><th>Tipo</th><th className="num">Valor</th><th className="num">Custo rateado</th></tr></thead>
          <tbody>{(note.trades ?? []).map((t) => (
            <tr key={t.line}><td>{t.side}</td><td>{t.instrument}</td><td>{fmtDay(t.maturity)}</td><td className="num">{t.quantity}</td><td className="num tabular">{t.price.replace(".", ",")}</td>
              <td>{t.tradeType === "day_trade" ? "Day trade" : "Normal"}</td><td className={`num tabular ${sign(t.amount)}`}>{brl(t.amount)}</td><td className="num tabular">{t.cost ? brl(t.cost) : ""}</td></tr>))}</tbody>
        </table></div>
        {(note.transactions ?? []).length > 0 && (
          <>
            <h4>Lançamentos gerados</h4>
            <ul className="small">{note.transactions!.map((t) => <li key={t.id}>{t.description}: <b>{t.kind === "receita" ? "+" : "−"} {brl(t.amount)}</b>{t.status === "cancelado" ? " (cancelado)" : t.status === "conciliado" ? " · conciliado" : " · confirmado"}</li>)}</ul>
          </>
        )}
        <div className="modal-actions">
          <a className="btn ghost" href={`/api/${realm}/trading-notes/${note.id}/pdf`} target="_blank" rel="noreferrer">Ver PDF</a>
          <Btn onClick={onClose}>Fechar</Btn>
        </div>
      </div>
    </Modal>
  );
}

function DeleteNote({ note, onClose, onConfirm }: { note: TradingNote; onClose: () => void; onConfirm: (reason: string) => Promise<void> }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  return (
    <Modal title="Excluir nota" onClose={onClose}>
      <form onSubmit={(e) => { e.preventDefault(); if (reason.trim().length < 3) return; setBusy(true); void onConfirm(reason.trim()).finally(() => setBusy(false)); }}>
        <p className="small">Os lançamentos da nota {note.noteNumber} ({fmtDay(note.tradeDate)}, líquido {brl(note.net)}) serão cancelados e o saldo da conta volta ao que era. O histórico fica preservado.</p>
        <Field label="Motivo da exclusão (mínimo 3 letras)" required><Input value={reason} onChange={(e) => setReason(e.target.value)} maxLength={300} name="reason" /></Field>
        <div className="modal-actions"><Btn variant="ghost" onClick={onClose}>Voltar</Btn><button type="submit" className="btn danger" disabled={busy || reason.trim().length < 3} data-note-delete-confirm>Confirmar exclusão</button></div>
      </form>
    </Modal>
  );
}
